import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import config from "../../config.js";
import { useNavigate } from "react-router-dom";
import {
  MANDATORY_FEATURE_CATEGORIES,
  getFeaturesForCategory,
  getOrderedFeatureCategories,
  getPredefinedFeatureCategories,
} from "../../utils/featureCategories";
import FeatureCategoryGrid from "../features/FeatureCategoryGrid";

export default function RegisterFeatures({ organizationDetails }) {
  const navigate = useNavigate();
  const [allFeatures, setAllFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [selectedCategories, setSelectedCategories] = useState({});

  const [saveButtonText, setSaveButtonText] = useState("Save Draft");
  const [registerOrganizationTypes, setRegisterOrganizationTypes] = useState([]);

  // Define mandatory categories
  const mandatoryCategories = MANDATORY_FEATURE_CATEGORIES;

  const isSchoolTrackingSelected = registerOrganizationTypes.includes("schoolTrack");

  const predefinedCategories = getPredefinedFeatureCategories({
    includeSchoolCategories: isSchoolTrackingSelected,
  });

  useEffect(() => {
    const storedOrganizationTypes = localStorage.getItem("registerOrganizationTypes");
    const storedSelectedApp = localStorage.getItem("selectedApp");

    if (storedOrganizationTypes) {
      try {
        const parsedOrganizationTypes = JSON.parse(storedOrganizationTypes);
        setRegisterOrganizationTypes(
          Array.isArray(parsedOrganizationTypes) ? parsedOrganizationTypes : []
        );
      } catch (parseError) {
        console.error("Error parsing registration organization types:", parseError);
        setRegisterOrganizationTypes(
          storedSelectedApp ? [storedSelectedApp] : []
        );
      }
    } else if (storedSelectedApp) {
      setRegisterOrganizationTypes([storedSelectedApp]);
    }

    getAllTheAvailableFeatures();
  }, []);

  useEffect(() => {
    if (allFeatures.length > 0) {
      const finalCategories = getOrderedFeatureCategories(allFeatures, {
        includeSchoolCategories: isSchoolTrackingSelected,
      });
      console.log("Final Categories:", finalCategories); // Debugging

      const initialCategoryStates = {};
      finalCategories.forEach((category) => {
        // Mark mandatory categories as selected by default
        initialCategoryStates[category] = mandatoryCategories.includes(category);
      });

      setCategories(finalCategories);
      setSelectedCategories(initialCategoryStates);

      // Also mark all features from mandatory categories as selected
      const initialFeatureStates = {};
      mandatoryCategories.forEach((category) => {
        const categoryFeatures = getFeaturesForCategory(allFeatures, category).filter(
          (feature) => feature.isDefault === true
        );

        categoryFeatures.forEach((feature) => {
          initialFeatureStates[feature.id] = true;
        });
      });

      setSelectedFeatures(initialFeatureStates);
      setLoading(false);
    } else {
      const initialCategoryStates = {};
      predefinedCategories.forEach((category) => {
        initialCategoryStates[category] = mandatoryCategories.includes(category);
      });

      // Ensure 'admin' is last even when no features are available
      const finalCategories = [
        ...predefinedCategories.filter((cat) => cat !== "admin"),
        "admin",
      ];
      setCategories(finalCategories);
      setSelectedCategories(initialCategoryStates);
      setLoading(false);
    }
  }, [allFeatures, isSchoolTrackingSelected]);

  useEffect(() => {
    const selectedIds = getSelectedFeatureIds();
    console.log("Current selected feature IDs (state change):", selectedIds);
  }, [selectedFeatures, selectedCategories]);

  const getAllTheAvailableFeatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/feature/`, {
        withCredentials: true,
      });
      setAllFeatures(response.data);
      console.log("Fetched features:", response.data);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("Unauthorized request");
        setError("Unauthorized request");
      } else {
        console.error("Error fetching available features:", error);
        setError("Failed to load features");
      }
      setLoading(false);
    }
  };

  const getFeaturesByCategory = (category) => {
    return getFeaturesForCategory(allFeatures, category);
  };

  const handleCategoryChange = (category) => {
    // Don't allow deselecting mandatory categories
    if (mandatoryCategories.includes(category)) {
      return;
    }

    const newState = !selectedCategories[category];
    setSelectedCategories((prev) => ({
      ...prev,
      [category]: newState,
    }));

    setSelectedFeatures((prev) => {
      const updatedFeatures = { ...prev };
      const categoryFeatures = getFeaturesByCategory(category);
      const regularFeatures = categoryFeatures.filter(
        (feature) => feature.isDefault === true
      );
      regularFeatures.forEach((feature) => {
        if (newState) {
          updatedFeatures[feature.id] = true;
        } else {
          if (feature.isDefault === true) {
            delete updatedFeatures[feature.id];
          }
        }
      });
      return updatedFeatures;
    });
  };

  const handleFeatureChange = (featureId) => {
    setSelectedFeatures((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  };

  const getSelectedFeatureIds = () => {
    const selectedIds = new Set();

    categories.forEach((category) => {
      if (selectedCategories[category]) {
        const categoryFeatures = getFeaturesByCategory(category);
        const regularFeatures = categoryFeatures.filter(
          (feature) => feature.isDefault === true
        );
        regularFeatures.forEach((feature) => {
          selectedIds.add(feature.id);
        });
      }
    });

    Object.keys(selectedFeatures).forEach((id) => {
      if (selectedFeatures[id]) {
        selectedIds.add(parseInt(id));
      }
    });

    return Array.from(selectedIds);
  };

  const handleSave = async () => {
    setSaveButtonText("Saving...");
    const selectedIds = getSelectedFeatureIds();
    console.log("Selected feature IDs on save:", selectedIds);

    try {
      const organizationId = localStorage.getItem("registerOrganizationId");
      if (!organizationId) {
        throw new Error("Organization ID not found. Please register first.");
      }

      const response = await axios.put(
        `${config.baseURL}/register/${organizationId}`,
        {
          requestedFeatureIds: selectedIds,
        },
        { withCredentials: true }
      );

      console.log("Draft saved:", response.data);
      // alert("Draft saved successfully!");
      setSaveButtonText("Saved"); // Change button text to "Saved"
      // Revert to "Save Draft" after 2 seconds
      setTimeout(() => {
        setSaveButtonText("Save Draft");
      }, 2000);
    } catch (error) {
      console.error("Error saving draft:", error);
      setError(error.response?.data?.msg || "Failed to save draft");
      setSaveButtonText("Save Draft")
    }
  };

  const handleTellUsMore = async () => {
    const selectedIds = getSelectedFeatureIds();
    console.log("Selected feature IDs before redirect:", selectedIds);

    try {
      const organizationId = localStorage.getItem("registerOrganizationId");
      if (!organizationId) {
        throw new Error("Organization ID not found. Please register first.");
      }

      const response = await axios.put(
        `${config.baseURL}/register/${organizationId}`,
        {
          requestedFeatureIds: selectedIds,
        },
        { withCredentials: true }
      );

      console.log("Features updated:", response.data);
      navigate("/registerneedinfo");
    } catch (error) {
      console.error("Error updating features:", error);
      setError(error.response?.data?.msg || "Failed to update features");
    }
  };

  useEffect(() => {
    const organizationId = localStorage.getItem("registerOrganizationId");
    if (!organizationId) {
      navigate("/register");
    }
  }, [navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading features...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center", color: "error.main" }}>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F2F2F3", padding: "20px 0" }}>
      <Paper
        sx={{
          borderRadius: "8px",
          overflow: "hidden",
          margin: "0 40px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
        }}
      >
        <Box sx={{ padding: "20px" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "25px",
                    color: "#183059",
                  }}
                >
                  Select Features For Your Requirement
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: "15px",
                    color: "#1F242E",
                  }}
                >
                  Choose The Features That Fit Your Needs
                </Typography>
                <Typography
                  variant="body2"
                  color="error"
                  gutterBottom
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    fontStyle: "italic",
                    color: "#FF0000",
                  }}
                >
                  <b style={{ fontWeight: 600, fontStyle: "normal" }}>Note:</b>{" "}
                  These Features Can't Be Changed Later, So Choose Details
                  Carefully.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FeatureCategoryGrid
                categories={categories}
                mandatoryCategories={mandatoryCategories}
                getCategoryFeatures={getFeaturesByCategory}
                getCategoryState={(category, regularFeatures, addOnFeatures) => ({
                  checked: selectedCategories[category] || false,
                  disabled:
                    regularFeatures.length === 0 && addOnFeatures.length === 0,
                })}
                onCategoryToggle={(category) => handleCategoryChange(category)}
                isFeatureSelected={(_, feature) => selectedFeatures[feature.id]}
                onAddOnToggle={(_, feature) => handleFeatureChange(feature.id)}
                cardSx={{
                  minHeight: "150px",
                }}
                headerPaddingByCategory={(_, isMandatory) =>
                  isMandatory ? "8px 20px" : "4px"
                }
                titleSx={{
                  fontWeight: 700,
                  fontSize: "16px",
                }}
                bodySx={{
                  pl: 2,
                  gap: "8px",
                }}
                regularFeatureSx={(isSelected) => ({
                  mb: 1,
                  color: "#111214",
                  fontWeight: isSelected ? 500 : 400,
                })}
                addOnHeaderSx={{
                  borderRadius: "4px",
                }}
                addOnTitleSx={{
                  fontWeight: 700,
                  fontSize: "14px",
                }}
                addOnFeatureSx={(isSelected) => ({
                  color: isSelected ? "#111214" : "#666",
                  fontWeight: isSelected ? 500 : 400,
                })}
                footer={
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      sx={{
                        width: "240px",
                        height: "55px",
                        borderRadius: "8px",
                        background: "#FFFFFF",
                        border: "1px solid #414958",
                        color: "#111214",
                        textTransform: "none",
                      }}
                      onClick={handleSave}
                      disabled={saveButtonText === "Saving..."}
                    >
                      {saveButtonText === "Saving..." && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      {saveButtonText}
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        width: "240px",
                        height: "55px",
                        borderRadius: "8px",
                        background: "#F9E719",
                        border: "1px solid #414958",
                        color: "#111214",
                        "&:hover": { background: "#F9E719" },
                        textTransform: "none",
                      }}
                      onClick={handleTellUsMore}
                    >
                      Tell us more...
                    </Button>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}
